"use client";
import Image from "next/image";
import { useEffect, useRef, useState } from "react";
import { useGlobalContext } from "@aleksundshantu/aunds-utils-react/contexts";
import { useAppContext } from "@/context/AppContext";
import styles from "./Modal.module.scss";
import dynamic from "next/dynamic";
import { CustomLink } from "@/components/CustomLink";
import { CustomSVG } from "@/components/_primitives/CustomSVG/CustomSVG.client";

const VidstackPlayer = dynamic(
  () => import("../../../components/VidstackPlayer/VidstackPlayer"),
  { ssr: false },
);

export type ModalType = {
  info: {
    src: string;
    caption: string;
    href: string;
    videoSrc?: string;
  };
  open: boolean;
};

export const Modal = () => {
  const modalRef = useRef<HTMLDivElement>(null);
  const { getIcon, getLabel } = useGlobalContext();
  const { instagramModal, setInstagramModal } = useAppContext();
  const [isExiting, setIsExiting] = useState(false);

  useEffect(() => {
    if (modalRef.current && instagramModal) {
      modalRef.current.focus();
    }
  }, [instagramModal]);

  const handleClose = () => {
    setIsExiting(true);
    setTimeout(() => {
      setInstagramModal(null);
      setIsExiting(false);
    }, 600); // Match animation duration
  };

  return (
    <>
      {instagramModal && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 9999,
          }}
          className={`${styles["insta-modal-wrapper"]} ${
            isExiting ? styles["modal-exit"] : styles["modal-enter"]
          }`}
        >
          <div
            ref={modalRef}
            tabIndex={0}
            role="button"
            className={styles["insta-modal"]}
            onClick={handleClose}
            onKeyDown={(e) => {
              if (e.key === "Escape") {
                handleClose();
              }
            }}
          >
            {instagramModal.info.videoSrc ? (
              <VidstackPlayer
                autoplay={false}
                loop={true}
                muted={false}
                video_decorative={true}
                video={{
                  copyright: "", // hotfix, pls set correctly
                  audio: true,
                  audio_description_video_desktop: false,
                  captions: false,
                  description: instagramModal.info.caption,
                  transcript: { textfile: false, text: "" },
                  contains_fast_moves: false,
                  contains_flashing_content: false,
                  contains_problematic_content: false,
                  internes_video_desktop: {
                    url: instagramModal.info.videoSrc,
                    alt: "",
                    title: "",
                    author: "",
                    description: instagramModal.info.caption,
                    filename: "",
                    id: "",
                    link: "",
                  },
                  video_thumbnail_desktop: {
                    url: instagramModal.info.src,
                    width: 0,
                    height: 0,
                    alt: instagramModal.info.caption,
                    type: "image",
                    author: "",
                    caption: instagramModal.info.caption,
                    date: "",
                    description: "",
                    filename: "",
                    id: "",
                    link: "",
                    title: "",
                    image_blur: "",
                    mime_type: "image/webp",
                    mimetype: "image/webp",
                    modified: "",
                    name: "",
                    acf: { link_1: "", link_2: "", text_1: "", text_2: "" },
                  },
                  video_thumb_extern: false,
                  video_type: "upload",
                  externes_video: null,
                  extern_video_audio_description: null,
                  externes_video_iframe: "",
                  internes_video_tablet: false,
                  externes_video_iframe_audio_description: "",
                  has_single_page: false,
                  internes_video_fullhd: false,
                  internes_video_mobile: false,
                  video_thumb_fullhd: false,
                  video_thumb_tablet: false,
                  video_thumb_mobile: false,
                  seo_settings: {
                    breadcrumb_title: "",
                    seo_title: "",
                    seo_metadescription: "",
                    social_media_share_description: "",
                    exclude_from_search_engines: false,
                    social_media_share_image: false,
                    show_in_sitemap: false,
                  },
                }}
              />
            ) : (
              <CustomLink
                href={instagramModal.info.href}
                target="_blank"
                rel="noreferrer noopener"
                aria-label={`${getLabel("open_project")} ${instagramModal.info.caption} in Instagram`}
              >
                <Image
                  src={instagramModal.info.src}
                  alt={instagramModal.info.caption}
                  height={1200}
                  width={1200}
                  sizes="(max-width: 767px) 80vw,(max-width: 1215px) 75vw, 50vw"
                  quality={90}
                />
              </CustomLink>
            )}
          </div>
          <button
            type="button"
            onClick={handleClose}
            aria-label={getLabel("modal_close")}
            className={styles["insta-close"]}
          >
            <CustomSVG
              src={getIcon("close")}
              wrapper={"span"}
              aria-hidden="true"
              className="is-flex"
            />
          </button>
        </div>
      )}
    </>
  );
};
